import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { warehousesApi } from '../../api/warehouse';
import { genericDocumentApi } from '../../api/generic-documents';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { TagLabel } from '../../components/TagLabel';
import { DocumentsSection } from '../../components/DocumentsSection';

export function WarehouseWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const warehouseId = Number(id);
  const [showTagModal, setShowTagModal] = useState(false);

  const { data: warehouseData, isError } = useQuery({ queryKey: ['warehouse', warehouseId], queryFn: () => warehousesApi.get(warehouseId) });
  const warehouse = warehouseData?.data;
  const { data: inventoryData } = useQuery({ queryKey: ['warehouse-inventory', warehouseId], queryFn: () => warehousesApi.getInventory(warehouseId) });
  const inventory = inventoryData?.data ?? [];
  const { data: docsData } = useQuery({ queryKey: ['warehouse-documents', warehouseId], queryFn: () => genericDocumentApi.listFor('warehouse', warehouseId) });

  if (isError) return <div className="page"><div className="error-state">Failed to load this warehouse. Please try refreshing the page.</div></div>;
  if (!warehouse) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  return (
    <div className="page">
      <RecordHeader
        title={warehouse.name}
        readOnly={true}
        entityType="warehouse"
        entityId={warehouseId}
        actions={<button className="btn-secondary" onClick={() => setShowTagModal(true)}>Print Location Tag</button>}
        fields={[
          { label: 'Reference', value: warehouse.warehouse_reference },
          { label: 'Location', value: warehouse.location },
        ]}
      />

      {showTagModal && (
        <Modal title="Warehouse Location Tag" onClose={() => setShowTagModal(false)}>
          <TagLabel code={warehouse.warehouse_reference} title={warehouse.name} subtitle={warehouse.location} />
          <div className="form-actions">
            <button onClick={() => window.print()}>Print</button>
          </div>
        </Modal>
      )}

      <SectionHeader title="Current Stock Levels" />
      <DataTable
        rows={inventory}
        rowKey={(i) => i.item_id}
        emptyMessage="No stock recorded for this warehouse yet."
        columns={[
          { header: 'Item Code', render: (i) => i.item_code },
          { header: 'Name', render: (i) => i.name },
          { header: 'Quantity on Hand', render: (i) => `${i.quantity_on_hand} ${i.unit_of_measure}` },
          { header: 'Reorder Level', render: (i) => i.reorder_level ? `${i.reorder_level} ${i.unit_of_measure}` : '-' },
        ]}
      />

      <SectionHeader title="Documents" />
      <DocumentsSection
        documents={docsData?.data ?? []}
        downloadUrl={(documentId) => genericDocumentApi.downloadUrl('warehouse', documentId)}
        upload={(input) => genericDocumentApi.uploadFor('warehouse', warehouseId, input)}
        onDelete={(documentId) => genericDocumentApi.deleteFor('warehouse', documentId)}
        queryKeyToInvalidate={['warehouse-documents', warehouseId]}
        hrCategories={false}
      />
    </div>
  );
}
